/**
 * The Workspace right-hand panel (contracts-plan.md §11 tab 4): three tabs,
 * Changes (badge = counts.proposed), Notes, and a slot reserved for the
 * Phase 9 assistant (§4.8). `SegmentedTabs` switches; each tab's CONTENT
 * carries the `ws-panel-<tab>` testid (the tab buttons themselves are
 * SegmentedTabs' own — found by their label text in tests, same as every
 * other contracts suite's FilterSelect/aria-label convention).
 */
import { Sparkles } from 'lucide-react';
import { SegmentedTabs, EmptyState } from '../../../ui';
import { NotesPanel } from '../../components/notesPanel';
import type { NotesPanelSelection } from '../../components/notesPanel';
import { ChangesPanel } from './ChangesPanel';
import type { ChangesPanelProps } from './ChangesPanel';

export type PanelTab = 'changes' | 'notes' | 'assistant';

interface Props {
  panelTab: PanelTab;
  onPanelTabChange: (tab: PanelTab) => void;
  proposedCount: number;
  changesPanelProps: ChangesPanelProps;
  negotiationId: string;
  versionId: string;
  notesSelection?: NotesPanelSelection;
  readOnly: boolean;
}

export function SidePanel({
  panelTab,
  onPanelTabChange,
  proposedCount,
  changesPanelProps,
  negotiationId,
  versionId,
  notesSelection,
  readOnly,
}: Props) {
  return (
    <div data-testid="ws-side-panel" className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <SegmentedTabs
        label="Workspace panel"
        value={panelTab}
        onChange={(v) => onPanelTabChange(v as PanelTab)}
        tabs={[
          { value: 'changes', label: 'Changes', count: proposedCount },
          { value: 'notes', label: 'Notes' },
          { value: 'assistant', label: 'Assistant' },
        ]}
        className="px-2"
      />
      <div className="flex-1 overflow-y-auto">
        {panelTab === 'changes' && (
          <div data-testid="ws-panel-changes">
            <ChangesPanel {...changesPanelProps} />
          </div>
        )}
        {panelTab === 'notes' && (
          <div data-testid="ws-panel-notes">
            <NotesPanel negotiationId={negotiationId} versionId={versionId} selection={notesSelection} readOnly={readOnly} />
          </div>
        )}
        {panelTab === 'assistant' && (
          <div data-testid="ws-panel-assistant">
            <EmptyState
              icon={Sparkles}
              title="Assistant"
              hint="Reserved for the Phase 9 assistant (plan §4.8, WP-90): it will read the same workspace, changes and notes shown here. No model calls exist in this module before WP-90."
              variant="card"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default SidePanel;
